import {
  ChangeEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { DeepSearchContext } from '../context';

import {
  PageContainer,
  MessagesArea,
  MessageBubble,
  InputContainer,
  InputWrapper,
  StyledTextarea,
  SendButton,
  LandingContainer,
  LandingTitle,
  LandingSubtitle,
  TypingIndicator,
} from './DeepSearchPage.styles';

export const DeepSearchPage = () => {
  const { activeMessages, isLoading, sendMessage } =
    useContext(DeepSearchContext);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    document.body.classList.add('deepsearch-page');
    return () => {
      document.body.classList.remove('deepsearch-page');
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeMessages, isLoading]);

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }
    sendMessage(inputValue);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const showLanding = activeMessages.length === 0;

  return (
    <PageContainer>
      {showLanding ? (
        <LandingContainer>
          <LandingTitle>Deep Search Agent</LandingTitle>
          <LandingSubtitle>
            Ask anything to start a deep search. Get comprehensive results
            powered by AI.
          </LandingSubtitle>
        </LandingContainer>
      ) : (
        <MessagesArea>
          {activeMessages.map((message) => (
            <MessageBubble key={message.id} isUser={message.role === 'user'}>
              {message.content}
            </MessageBubble>
          ))}
          {isLoading && (
            <TypingIndicator>
              <span />
              <span />
              <span />
            </TypingIndicator>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>
      )}

      <InputContainer>
        <InputWrapper>
          <StyledTextarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            rows={1}
            aria-label="Search input"
          />
          <SendButton
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </SendButton>
        </InputWrapper>
      </InputContainer>
    </PageContainer>
  );
};
